/**
 * TodoStore — daemon-side proxy to the auth-server's /daemon/todos* endpoints.
 *
 * Source of truth is the auth-server. No local persistence, no EventEmitter.
 *
 * Each call resolves the workspace's repoKey via GitRemoteResolver, then proxies
 * to the auth-server. The repoKey is the git remote URL for git projects, or a
 * `.appostle/` name-marker for non-git projects (minted on first use). repoKey is
 * only null when neither could be established (e.g. the workspace dir is gone or
 * read-only) — list() returns an empty bucket and mutating calls throw
 * NoRemoteError in that case.
 *
 * NOTE: Cross-session push is not implemented in v1. When a session mutates a
 * todo, it re-fetches and pushes a fresh todo_list_state to itself. Other
 * sessions subscribed to the same workspaceId on the same daemon will only see
 * the update on their next mutation or reconnect. Phase 4 can add a broadcast.
 */

import type { Logger } from "pino";
import type {
  AuthServerActivityEntry,
  AuthServerTodoSession,
  AuthServerSubtodo,
  AuthServerTodo,
  AuthServerTodoClient,
} from "./auth-server-client.js";
import type { GitRemoteResolver } from "./git-remote-resolver.js";

export type TodoRecord = AuthServerTodo;
export type SubtodoRecord = AuthServerSubtodo;

export class NoRemoteError extends Error {
  constructor(cwd: string) {
    super(`no git remote found for cwd: ${cwd}`);
    this.name = "NoRemoteError";
  }
}

export class TodoStore {
  private readonly authServerTodoClient: AuthServerTodoClient;
  private readonly gitRemoteResolver: GitRemoteResolver;
  private readonly serverId: string;
  private readonly logger: Logger;

  constructor(options: {
    authServerTodoClient: AuthServerTodoClient;
    gitRemoteResolver: GitRemoteResolver;
    /** This daemon's serverId — stamped as createdByHostId on every new todo. */
    serverId: string;
    logger: Logger;
  }) {
    this.authServerTodoClient = options.authServerTodoClient;
    this.gitRemoteResolver = options.gitRemoteResolver;
    this.serverId = options.serverId;
    this.logger = options.logger.child({ module: "todo-store" });
    this.logger.debug("todo-store initialized (auth-server proxy)");
  }

  async list(
    workspaceId: string,
    cwd: string,
  ): Promise<{ repoKey: string | null; todos: TodoRecord[] }> {
    const repoKey = await this.gitRemoteResolver.resolve(workspaceId, cwd);
    if (repoKey === null) {
      return { repoKey: null, todos: [] };
    }
    const { todos } = await this.authServerTodoClient.listTodos(repoKey);
    return { repoKey, todos };
  }

  async add(input: {
    workspaceId: string;
    cwd: string;
    text: string;
    createdByAgentId?: string | null;
    createdByCwd?: string | null;
    linkedAgentHostId?: string | null;
    linkedAgentId?: string | null;
  }): Promise<TodoRecord> {
    const repoKey = await this.gitRemoteResolver.resolve(input.workspaceId, input.cwd);
    if (repoKey === null) {
      throw new NoRemoteError(input.cwd);
    }
    const trimmed = input.text.trim().slice(0, 500);
    if (!trimmed) {
      throw new Error("todo text is empty");
    }
    // Put new todos at the bottom of the list.
    const { todos } = await this.authServerTodoClient.listTodos(repoKey);
    const nextPosition = todos.length === 0 ? 0 : Math.max(...todos.map((t) => t.position)) + 1;
    return this.authServerTodoClient.createTodo({
      repoKey,
      text: trimmed,
      position: nextPosition,
      createdByAgentId: input.createdByAgentId ?? null,
      createdByCwd: input.createdByCwd ?? null,
      createdByHostId: this.serverId,
      linkedAgentHostId: input.linkedAgentHostId ?? null,
      linkedAgentId: input.linkedAgentId ?? null,
    });
  }

  async remove(todoId: string): Promise<void> {
    await this.authServerTodoClient.deleteTodo(todoId);
  }

  async setCompleted(todoId: string, completed: boolean): Promise<TodoRecord> {
    const completedAt = completed ? new Date().toISOString() : null;
    return this.authServerTodoClient.updateTodo(todoId, { completedAt });
  }

  async reorder(workspaceId: string, cwd: string, orderedTodoIds: string[]): Promise<void> {
    const repoKey = await this.gitRemoteResolver.resolve(workspaceId, cwd);
    if (repoKey === null) {
      throw new NoRemoteError(cwd);
    }
    await this.authServerTodoClient.reorderTodos(repoKey, orderedTodoIds);
  }

  async updateNotes(todoId: string, notes: string): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { notes });
  }

  async updateLinkedAgent(
    todoId: string,
    link: { linkedAgentHostId: string | null; linkedAgentId: string | null },
  ): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, {
      linkedAgentHostId: link.linkedAgentHostId,
      linkedAgentId: link.linkedAgentId,
    });
  }

  async updateAssignee(todoId: string, assignedToUserId: string | null): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { assignedToUserId });
  }

  async updateText(todoId: string, text: string): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { text: text.trim().slice(0, 500) });
  }

  async addSubtodo(todoId: string, text: string): Promise<SubtodoRecord> {
    // Use epoch-seconds as position so new subtodos naturally sort to the bottom.
    // Positions are just ordering hints; the client can reorder explicitly via
    // reorderSubtodos. Postgres int range comfortably fits epoch-second values
    // until year 2038, well beyond our pre-launch window.
    const position = Math.floor(Date.now() / 1000);
    return this.authServerTodoClient.createSubtodo(todoId, {
      text: text.trim().slice(0, 500),
      position,
    });
  }

  async removeSubtodo(subtodoId: string): Promise<void> {
    await this.authServerTodoClient.deleteSubtodo(subtodoId);
  }

  async setSubtodoCompleted(subtodoId: string, completed: boolean): Promise<SubtodoRecord> {
    const completedAt = completed ? new Date().toISOString() : null;
    return this.authServerTodoClient.updateSubtodo(subtodoId, { completedAt });
  }

  async updateSubtodoText(subtodoId: string, text: string): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, { text: text.trim().slice(0, 500) });
  }

  async reorderSubtodos(todoId: string, orderedSubtodoIds: string[]): Promise<void> {
    await this.authServerTodoClient.reorderSubtodos(todoId, orderedSubtodoIds);
  }

  async updateStatus(
    todoId: string,
    status: string,
    actor?: { actorAgentId?: string | null; actorHostId?: string | null },
  ): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { status, ...actor });
  }

  async updatePriority(todoId: string, priority: number): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { priority });
  }

  async updateSchedule(
    todoId: string,
    opts: {
      scheduledAt?: string | null;
      dueAt?: string | null;
      scheduleTargetHostId?: string | null;
    },
  ): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, opts);
  }

  async updateBlockedBy(todoId: string, blockedByTodoId: string | null): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { blockedByTodoId });
  }

  async updateResultReport(todoId: string, resultReport: string | null): Promise<TodoRecord> {
    return this.authServerTodoClient.updateTodo(todoId, { resultReport });
  }

  async listActivity(todoId: string): Promise<AuthServerActivityEntry[]> {
    const { activity } = await this.authServerTodoClient.listTodoActivity(todoId);
    return activity;
  }

  async listSessions(todoId: string): Promise<AuthServerTodoSession[]> {
    const { sessions } = await this.authServerTodoClient.listTodoSessions(todoId);
    return sessions;
  }

  async addComment(todoId: string, body: string): Promise<AuthServerActivityEntry> {
    return this.authServerTodoClient.addTodoComment(todoId, body);
  }

  async updateSubtodoNotes(subtodoId: string, notes: string): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, { notes });
  }

  async updateSubtodoAssignee(
    subtodoId: string,
    assignedToUserId: string | null,
  ): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, { assignedToUserId });
  }

  async updateSubtodoStatus(subtodoId: string, status: string): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, { status });
  }

  async updateSubtodoSchedule(
    subtodoId: string,
    opts: {
      scheduledAt?: string | null;
      dueAt?: string | null;
      scheduleTargetHostId?: string | null;
    },
  ): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, opts);
  }

  async updateSubtodoPriority(subtodoId: string, priority: number): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, { priority });
  }

  async updateSubtodoBlockedBy(
    subtodoId: string,
    blockedBySubtodoId: string | null,
  ): Promise<SubtodoRecord> {
    return this.authServerTodoClient.updateSubtodo(subtodoId, { blockedBySubtodoId });
  }
}
